import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import type { RoleListItemResponse } from '@portal/pkg-api/gen/admin-console'
import { useDeleteRole } from '../mutations'

interface DeleteRoleDialogProps {
  /** The role to delete, or null when the dialog is closed. */
  role: RoleListItemResponse | null
  onClose: () => void
}

/**
 * The server refuses a delete that would leave a member with no role at all, which is the refusal
 * this dialog most often surfaces: it cannot be predicted from the row, only reported.
 */
export function DeleteRoleDialog({ role, onClose }: DeleteRoleDialogProps) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const deleteRole = useDeleteRole()

  const handleConfirm = () => {
    if (!role) return
    deleteRole.mutate(
      { id: role.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('roles.table.roleDeleted'), { variant: 'success' })
          onClose()
        },
        onError: (error: unknown) => {
          const axiosError = error as { response?: { data?: { type?: string } } }
          const problemType = axiosError.response?.data?.type
          const key =
            problemType === 'https://gomercatus.com/problems/admin/roles/role-membership-required'
              ? 'roles.delete.membershipRequired'
              : 'roles.delete.failed'
          enqueueSnackbar(t(key), { variant: 'error' })
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open={role !== null} onClose={onClose}>
      <DialogTitle>{t('roles.delete.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('roles.delete.confirm', { name: role?.name ?? '' })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('roles.delete.cancel')}</Button>
        <Button variant="contained" color="error" onClick={handleConfirm}>
          {t('roles.delete.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
